import { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
  Box,
  Checkbox,
  Collapse,
  Text,
  Textarea,
  Divider,
} from '@chakra-ui/react';

import MultiSelect from 'components/fields/MultiSelect';

import DateField from 'components/fields/DateField';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import type { Watercraft } from 'api/quote-marketplace';
import { ValidatedField } from '.';
import { useValidationSchema } from '../hooks';

export interface WatercraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  watercraft: Watercraft;
  onChange: (watercraft: Watercraft) => void;
  onSave: () => void;
  propulsionTypeOptions: { value: string; label: string }[];
  mooringLocationOptions: { value: string; label: string }[];
  watercraftUsageOptions: { value: string; label: string }[];
  watersNavigatedOptions: { value: string; label: string }[];
  trailerConditionOptions?: { value: string; label: string }[];
  isNameRequired?: boolean;
  isValueRequired?: boolean;
  title?: string;
}

const WatercraftModal = ({
  isOpen,
  onClose,
  watercraft,
  onChange,
  onSave,
  propulsionTypeOptions,
  mooringLocationOptions,
  watercraftUsageOptions,
  watersNavigatedOptions,
  trailerConditionOptions = [],
  isNameRequired = false,
  isValueRequired = false,
  title = 'Watercraft',
}: WatercraftModalProps) => {
  const { isFieldRequired, isFieldVisible } = useValidationSchema();
  const isNameInvalid = isNameRequired && !watercraft.name?.trim();
  const isValueInvalid = isValueRequired && !watercraft.value_cad;

  const watercraftEntity = watercraft as unknown as Record<string, unknown>;
  const isUsageRequired = isFieldRequired(
    'location/watercraft',
    'watercraft_usage',
    watercraftEntity,
  );
  const isUsageInvalid =
    isUsageRequired && !watercraft.watercraft_usage?.length;

  const showHorsepower = isFieldVisible(
    'watercraft',
    'horsepower',
    watercraftEntity,
  );
  const showMaxSpeed = isFieldVisible(
    'watercraft',
    'max_speed_mph',
    watercraftEntity,
  );
  const showCommercialDescription = isFieldVisible(
    'watercraft',
    'describe_commercial_charter_use',
    watercraftEntity,
  );
  const showWeeksRented = isFieldVisible(
    'watercraft',
    'number_of_weeks_rented_to_others_per_annum',
    watercraftEntity,
  );

  const usageItems = useMemo(
    () =>
      watercraftUsageOptions.map((opt, index) => ({
        id: index,
        name: opt.label,
        value: opt.value,
      })),
    [watercraftUsageOptions],
  );

  const selectedUsageIds = useMemo(
    () =>
      (watercraft.watercraft_usage || [])
        .map((value) => usageItems.findIndex((item) => item.value === value))
        .filter((idx) => idx !== -1),
    [watercraft.watercraft_usage, usageItems],
  );

  const handleUsageChange = (selectedIds: number[]) => {
    const selectedValues = selectedIds
      .map((id) => usageItems[id]?.value)
      .filter(Boolean) as string[];
    onChange({
      ...watercraft,
      watercraft_usage: selectedValues,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          {watercraft.id ? `Edit ${title}` : `Add ${title}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem colSpan={2}>
              <FormControl
                isRequired={isNameRequired}
                isInvalid={isNameInvalid}
              >
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={watercraft.name}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      name: e.target.value,
                    })
                  }
                  placeholder="Watercraft name"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Manufacturer</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={watercraft.manufacturer}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      manufacturer: e.target.value,
                    })
                  }
                  placeholder="Manufacturer"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Make/Model</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={watercraft.make_model}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      make_model: e.target.value,
                    })
                  }
                  placeholder="Make and model"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Model Year</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={watercraft.model_year_yyyy ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      model_year_yyyy: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  placeholder="YYYY"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl
                isRequired={isValueRequired}
                isInvalid={isValueInvalid}
              >
                <FormLabel fontSize="sm">Value (CAD)</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={watercraft.value_cad ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      value_cad: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Length (ft)</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={watercraft.length_ft ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      length_ft: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  placeholder="Length in feet"
                />
              </FormControl>
            </GridItem>
            {showHorsepower && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Horsepower</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={watercraft.horsepower ?? ''}
                    onChange={(e) =>
                      onChange({
                        ...watercraft,
                        horsepower: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    placeholder="HP"
                  />
                </FormControl>
              </GridItem>
            )}
            {showMaxSpeed && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Max Speed (mph)</FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={watercraft.max_speed_mph ?? ''}
                    onChange={(e) =>
                      onChange({
                        ...watercraft,
                        max_speed_mph: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    placeholder="Max speed"
                  />
                </FormControl>
              </GridItem>
            )}
            <GridItem>
              <DateField
                label="Purchase Date"
                date={parseDateString(watercraft.purchase_date || '')}
                onChange={(date: Date | null) =>
                  onChange({
                    ...watercraft,
                    purchase_date: formatDateToString(date),
                  })
                }
                size="sm"
              />
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={watercraft.description}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      description: e.target.value,
                    })
                  }
                  placeholder="Additional description"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Propulsion Type</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={watercraft.propulsion_type || ''}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      propulsion_type: e.target.value,
                    })
                  }
                >
                  {propulsionTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/watercraft"
                fieldPath="mooring_location"
                label="Mooring Location"
                entity={watercraftEntity}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={watercraft.mooring_location || ''}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      mooring_location: e.target.value,
                    })
                  }
                >
                  {mooringLocationOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/watercraft"
                fieldPath="waters_navigated"
                label="Waters Navigated"
                entity={watercraftEntity}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={watercraft.waters_navigated || ''}
                  onChange={(e) =>
                    onChange({
                      ...watercraft,
                      waters_navigated: e.target.value,
                    })
                  }
                >
                  {watersNavigatedOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl
                isRequired={isUsageRequired}
                isInvalid={isUsageInvalid}
              >
                <FormLabel fontSize="sm">Watercraft Usage</FormLabel>
                <MultiSelect
                  selectedIds={selectedUsageIds}
                  availableItems={usageItems}
                  isLoading={false}
                  onChange={handleUsageChange}
                  label="Select usage..."
                  itemLabelSingular="usage"
                  itemLabelPlural="usages"
                />
              </FormControl>
            </GridItem>
          </Grid>

          {showCommercialDescription && (
            <>
              <Divider my={4} />
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Commercial/Charter Use
                </Text>
                <ValidatedField
                  itemPath="location/watercraft"
                  fieldPath="describe_commercial_charter_use"
                  label="Describe Commercial/Charter Use"
                  entity={watercraftEntity}
                >
                  <Textarea
                    variant="main"
                    size="sm"
                    value={watercraft.describe_commercial_charter_use || ''}
                    onChange={(e) =>
                      onChange({
                        ...watercraft,
                        describe_commercial_charter_use: e.target.value,
                      })
                    }
                    placeholder="Describe how the watercraft is used commercially"
                  />
                </ValidatedField>
              </Box>
            </>
          )}

          <Divider my={4} />
          <Box>
            <Checkbox
              isChecked={watercraft.rented_or_leased_to_others}
              onChange={(e) =>
                onChange({
                  ...watercraft,
                  rented_or_leased_to_others: e.target.checked,
                })
              }
              mb={2}
            >
              <Text fontWeight="medium">Rented or leased to others</Text>
            </Checkbox>
            {showWeeksRented && (
              <Box pl={6} pt={2}>
                <ValidatedField
                  itemPath="location/watercraft"
                  fieldPath="number_of_weeks_rented_to_others_per_annum"
                  label="Weeks Rented Per Year"
                  entity={watercraftEntity}
                >
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={
                      watercraft.number_of_weeks_rented_to_others_per_annum ??
                      ''
                    }
                    onChange={(e) =>
                      onChange({
                        ...watercraft,
                        number_of_weeks_rented_to_others_per_annum: e.target
                          .value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    placeholder="Number of weeks"
                  />
                </ValidatedField>
              </Box>
            )}
          </Box>

          <Divider my={4} />
          <Box>
            <Checkbox
              isChecked={watercraft.has_trailer}
              onChange={(e) =>
                onChange({
                  ...watercraft,
                  has_trailer: e.target.checked,
                })
              }
              mb={2}
            >
              <Text fontWeight="medium">Has Trailer</Text>
            </Checkbox>
            <Collapse in={watercraft.has_trailer} animateOpacity>
              <Box pl={6} pt={2}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <ValidatedField
                      itemPath="location/watercraft"
                      fieldPath="trailer_value_cad"
                      label="Trailer Value (CAD)"
                      entity={watercraftEntity}
                    >
                      <Input
                        variant="main"
                        size="sm"
                        type="number"
                        value={watercraft.trailer_value_cad ?? ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_value_cad: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </ValidatedField>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Length (ft)</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        type="number"
                        value={watercraft.trailer_length_ft ?? ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_length_ft: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Manufacturer</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        value={watercraft.trailer_manufacturer || ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_manufacturer: e.target.value,
                          })
                        }
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Make/Model</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        value={watercraft.trailer_make_model || ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_make_model: e.target.value,
                          })
                        }
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Model Year</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        type="number"
                        value={watercraft.trailer_model_year_yyyy ?? ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_model_year_yyyy: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                        placeholder="YYYY"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Condition</FormLabel>
                      <Select
                        variant="main"
                        size="sm"
                        value={watercraft.trailer_condition || ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_condition: e.target.value,
                          })
                        }
                      >
                        <option value="">Select condition</option>
                        {trailerConditionOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">License Plate</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        value={watercraft.trailer_license_plate || ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_license_plate: e.target.value,
                          })
                        }
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Serial Number</FormLabel>
                      <Input
                        variant="main"
                        size="sm"
                        value={watercraft.trailer_serial_number || ''}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_serial_number: e.target.value,
                          })
                        }
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <DateField
                      label="Purchase Date"
                      date={parseDateString(
                        watercraft.trailer_purchase_date || '',
                      )}
                      onChange={(date: Date | null) =>
                        onChange({
                          ...watercraft,
                          trailer_purchase_date: formatDateToString(date),
                        })
                      }
                      size="sm"
                    />
                  </GridItem>
                  <GridItem>
                    <FormControl pt={6}>
                      <Checkbox
                        isChecked={watercraft.trailer_is_road_licensed}
                        onChange={(e) =>
                          onChange({
                            ...watercraft,
                            trailer_is_road_licensed: e.target.checked,
                          })
                        }
                      >
                        Road Licensed
                      </Checkbox>
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="yellow" onClick={onSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WatercraftModal;
